/**
 * Predimensionamiento de muros (placas de concreto armado).
 *
 *   tw_min = max(h / 25, 15 cm)        (E.060 14.5.3.1)
 *
 * Donde h es la altura libre del muro. Adicional:
 *   - Para muros sismorresistentes (E.060 21.9), tw ≥ 15 cm
 *   - El cociente hw / lw debe revisarse para esbeltez (típico 1.5 – 4)
 */

import { useState } from 'react';
import { PredimCard, Group, NumberRow, ResultRow, FormulaNote, roundTo } from './shared';
import { WallElevationDiagram, DiagramPanel, View2D3DToggle } from './diagrams';
import { Wall3D } from './views3d';
import { PredimToolbar } from './PredimToolbar';
import type { DesignFull } from '@/features/designs/types';

type Variant = 'placa';

interface PredimWallProps {
  variant: Variant;
  initialInputs?: { hw?: number; lw?: number } | null;
  existing?: DesignFull | null;
}

export function PredimWall({ variant, initialInputs, existing }: PredimWallProps) {
  void variant;
  const [hw, setHw] = useState(initialInputs?.hw ?? 280);    // altura libre cm
  const [lw, setLw] = useState(initialInputs?.lw ?? 400);    // longitud cm

  const tw_min = Math.max(hw / 25, 15);
  const tw_round = roundTo(tw_min, 5);   // round to 5 cm multiple
  const tw_practical = Math.max(tw_round, 15);

  const slenderness = hw / tw_practical;
  const aspect = hw / lw;

  const exportInputs = { wall_variant: 'placa', hw, lw };
  const [view, setView] = useState<'2D' | '3D'>('2D');

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex justify-end">
        <PredimToolbar
          family="walls"
          variant="placa"
          inputs={exportInputs}
          existing={existing}
        />
      </div>
      <PredimCard
        title="Placa de concreto armado"
        subtitle="Espesor mínimo por esbeltez (E.060 14.5.3.1). Para muros sismorresistentes E.060 21.9, tw ≥ 15 cm."
      >
        <Group title="Geometría del muro">
          <NumberRow label="hw — altura libre" value={hw} onChange={setHw} step={10} unit="cm" />
          <NumberRow label="lw — longitud" value={lw} onChange={setLw} step={25} unit="cm" />
        </Group>

        <FormulaNote>
          <span className="font-mono">tw_min = max(hw / 25, 15 cm)</span>
        </FormulaNote>
      </PredimCard>

      <PredimCard title="Resultados">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_auto]">
          <div className="space-y-1">
            <ResultRow label="tw mínimo teórico" value={`${tw_min.toFixed(2)} cm`} />
            <ResultRow label="tw recomendado" value={`${tw_practical} cm`} highlight />
            <ResultRow label="Esbeltez hw / tw" value={`${slenderness.toFixed(1)} (lim 25)`} />
            <ResultRow label="Relación hw / lw" value={aspect.toFixed(3)} />
          </div>
          <DiagramPanel
            title={view === '3D' ? 'Vista 3D de la placa' : 'Elevación de la placa'}
            toolbar={<View2D3DToggle value={view} onChange={setView} />}
          >
            {view === '3D' ? (
              <div style={{ width: 380, height: 320 }}>
                <Wall3D hw={hw} lw={lw} tw={tw_practical} />
              </div>
            ) : (
              <WallElevationDiagram hw={hw} lw={lw} tw={tw_practical} />
            )}
          </DiagramPanel>
        </div>

        {slenderness > 25 && (
          <div className="mt-3 rounded-[var(--radius-sm)] border p-2.5 text-[11px]"
            style={{
              background: 'rgba(220,38,38,0.10)',
              borderColor: 'rgba(220,38,38,0.40)',
              color: 'var(--text-primary)',
            }}>
            <strong style={{ color: 'var(--danger)' }}>Esbeltez excede 25.</strong> Aumentar tw o
            reducir la altura libre.
          </div>
        )}
        {aspect > 4 && (
          <div className="mt-2 rounded-[var(--radius-sm)] border p-2.5 text-[11px]"
            style={{
              background: 'rgba(245,158,11,0.08)',
              borderColor: 'rgba(245,158,11,0.40)',
              color: 'var(--text-primary)',
            }}>
            <strong style={{ color: 'var(--accent)' }}>Atención:</strong> con hw/lw &gt; 4 el muro
            es muy esbelto y trabaja como columna. Considerar la limitación.
          </div>
        )}

        <div className="mt-3 text-[11px] leading-relaxed" style={{ color: 'var(--text-muted)' }}>
          Para diseño completo con flexo-compresión P-M, cortante en el plano y elementos de borde
          (E.060 21.9), usa el módulo <strong>Muros</strong>.
        </div>
      </PredimCard>
    </div>
  );
}
